// File: js/sync.js
//
// A THIN ADAPTER over `db/sync.js`. It holds no sync logic of its own — spec §13
// step 4: the UI modules "add no storage or sync logic of their own". Everything
// this file once did has moved:
//
//   • its own `new Dexie('HospitalRevenueDB')` with a THIRD primary key for
//     `transactions` → `db/schema.js` (one name, one chain) — audit C5;
//   • the `23505` branch that wrote `sync_status:'conflict'`, a status NOTHING in
//     the codebase ever read → `db/sync.js`, which parks the event in a table a
//     human sees, and this file's job is now to MAKE them see it — audit C1;
//   • `updateStockForSale` calling `decrement_drug_stock(p_drug_id: item.id)` on
//     items that carried no `id` at all, with the error swallowed per item and the
//     row still marked `'synced'` → deleted. Stock is a fold of the ledger
//     (spec §4), so a synced sale moves stock by existing — audit C4;
//   • « Sync complete. 0 record(s) uploaded » reported as success on every failed
//     cycle → the real state, with backoff, from `db/sync.js` — audit M1.
//
// What is LEFT here is the part the audit says was missing entirely: making the
// queue visible. A conflict a human cannot see is the C1 failure, and a count with
// no amount is the H6 failure — a cashier who worked an offline morning reading
// « Recettes du jour : 0 FCFA » with a full drawer.

import { bootEngine, getEngine, engineStatus } from './ui/engine-boot.js';
import { formatCurrency } from './utils.js';
import {
    UI_STRINGS,
    conflictQueueNotice,
    pendingInTotalNotice
} from './ui/engine-messages.js';

/** How often the banners are re-read from the tables. */
const UI_REFRESH_MS = 10000;

/**
 * Set a banner's text and visibility.
 *
 * `textContent`, never `innerHTML` (audit M7): these strings interpolate counts
 * and amounts, and one day someone will interpolate a patient name here.
 *
 * @param {string} id Element id.
 * @param {boolean} visible
 * @param {string} [text]
 */
function setBanner(id, visible, text) {
    const el = typeof document !== 'undefined' ? document.getElementById(id) : null;
    if (!el) return;
    if (typeof text === 'string') {
        const slot = el.querySelector('.alert-message') || el;
        slot.textContent = text;
    }
    el.classList.toggle('hidden', !visible);
}

/** @param {string|null} iso @returns {string} */
function formatInstant(iso) {
    if (!iso) return 'jamais';
    const ms = Date.parse(iso);
    if (Number.isNaN(ms)) return 'jamais';
    return new Date(ms).toLocaleTimeString('fr-CM', { hour: '2-digit', minute: '2-digit' });
}

export const SyncService = {
    /** @type {number|null} */
    refreshTimer: null,
    /** @type {(() => void)|null} */
    unsubscribe: null,
    /** @type {(() => void)|null} Held so `stop()` can detach it — a sign-in/out
     *  cycle would otherwise stack a new online listener every time. */
    connectivityHandler: null,
    started: false,

    /**
     * Boot the engine (idempotent) and start publishing its state to the banners.
     *
     * @param {{role?:string|null, deviceName?:string|null}} [options]
     * @returns {Promise<object|null>}
     */
    async init(options = {}) {
        let engine = null;
        try {
            engine = await bootEngine(options);
        } catch (error) {
            console.error('Money engine boot failed:', error);
        }

        if (!this.started) {
            this.started = true;
            if (typeof window !== 'undefined') {
                // Online/offline flips change what the banners must say even when
                // no round has run, so they redraw on the event itself.
                this.connectivityHandler = () => void this.refreshUI();
                window.addEventListener('online', this.connectivityHandler);
                window.addEventListener('offline', this.connectivityHandler);
            }
            this.refreshTimer = setInterval(() => void this.refreshUI(), UI_REFRESH_MS);
        }

        if (engine && !this.unsubscribe) {
            this.unsubscribe = engine.onStatus(status => this.render(status));
        }

        await this.refreshUI();
        return engine;
    },

    /** Push + pull now (the manual "synchroniser" affordance and the online event). */
    async runSync() {
        const engine = getEngine();
        if (!engine) return;
        await engine.kick();
    },

    /** Re-read the tables and redraw. */
    async refreshUI() {
        this.render(await engineStatus());
    },

    /**
     * Draw the engine's state. Five independent facts, five banners — deliberately
     * NOT collapsed into one line, because "you cannot collect" and "you have
     * unsent money" call for different actions from the same person.
     *
     * @param {object} status From `engineStatus()`.
     */
    render(status) {
        if (!status) return;

        // 1. Durability. The hardest refusal: this device may lose cash it takes.
        setBanner('engine-banner-storage', !status.persistGranted, UI_STRINGS.STORAGE_NOT_DURABLE);

        // 2. Identity. Without it a parked sale carries a null device_id and shows
        //    up in NO device's queue (spec §7).
        setBanner(
            'engine-banner-device',
            status.ready && !status.deviceId,
            'Cet appareil n\'est pas encore enregistré — un administrateur doit l\'enregistrer avant tout encaissement. Utilisez le carnet de reçus papier en attendant.'
        );

        // 3. Receipt supply. Exhausted is a refusal; low is a warning.
        const block = status.block || { exhausted: true, low: true, remaining: 0 };
        setBanner(
            'engine-banner-receipts',
            block.exhausted || block.low,
            block.exhausted
                ? UI_STRINGS.BLOCK_EXHAUSTED
                : `Bientôt à court de numéros de reçu (${block.remaining} restants). ${UI_STRINGS.RECEIPT_ISSUED}`
        );

        // 4. Pending money — the COUNT AND THE AMOUNT (audit H6). A count alone is
        //    what let « Recettes du jour : 0 FCFA » stand next to a full drawer.
        const pendingParts = [];
        if (!status.online) pendingParts.push('Hors ligne.');
        if (status.pendingCount > 0) {
            pendingParts.push(
                `${status.pendingCount} vente(s) en attente d'envoi — ${formatCurrency(status.pendingTotal)}.`,
                UI_STRINGS.SAVED_OFFLINE
            );
        } else if (!status.online) {
            pendingParts.push('Aucune vente en attente.');
        }
        setBanner(
            'engine-banner-pending',
            pendingParts.length > 0,
            pendingParts.join(' ')
        );

        // 5. The conflict queue. This is the banner audit C1 says did not exist:
        //    `'conflict'` was written once and read nowhere, so the badge returned
        //    to 0 and the cash vanished quietly.
        setBanner(
            'engine-banner-conflicts',
            status.conflictCount > 0,
            `${conflictQueueNotice(status.conflictCount)} — enregistrement(s) refusé(s) par le serveur. Prévenez l'administrateur.`
        );

        // The legacy dashboard tile + the header online indicator.
        const pendingTile = typeof document !== 'undefined'
            ? document.querySelector('[data-stat="sync-pending"]')
            : null;
        if (pendingTile) pendingTile.textContent = String(status.pendingCount);

        const pendingAmount = typeof document !== 'undefined'
            ? document.getElementById('sync-pending-amount')
            : null;
        if (pendingAmount) {
            pendingAmount.textContent = status.pendingCount > 0
                ? `${formatCurrency(status.pendingTotal)} — ${pendingInTotalNotice(status.pendingCount)}`
                : '';
        }

        // The count is ALWAYS written. Hiding it at zero left the label
        // « Conflits à traiter : » trailing off under a red dot, which reads as a
        // broken widget rather than as "none". Zero is an answer; the colour, not
        // the presence of the number, carries whether it is a good one.
        const conflictBadge = typeof document !== 'undefined'
            ? document.getElementById('sync-conflict-count')
            : null;
        if (conflictBadge) {
            conflictBadge.textContent = String(status.conflictCount);
            conflictBadge.classList.toggle('badge-danger', status.conflictCount > 0);
            conflictBadge.classList.toggle('badge-success', status.conflictCount === 0);
        }

        const conflictDot = typeof document !== 'undefined'
            ? document.getElementById('sync-conflict-dot')
            : null;
        if (conflictDot) {
            conflictDot.classList.toggle('bg-red-500', status.conflictCount > 0);
            conflictDot.classList.toggle('bg-green-500', status.conflictCount === 0);
        }

        // The header bell. It shipped with a permanently-hidden `0` that no code
        // ever revealed — a notification counter that could not notify. It is fed
        // from the SAME status snapshot as the banners rather than from a count of
        // its own, so the bell and the banner can never disagree about how much is
        // outstanding.
        const headerBadge = typeof document !== 'undefined'
            ? document.getElementById('notification-badge')
            : null;
        if (headerBadge) {
            const outstanding = status.pendingCount + status.conflictCount;
            headerBadge.textContent = String(outstanding);
            headerBadge.classList.toggle('hidden', outstanding === 0);
            headerBadge.title = `${status.pendingCount} en attente d'envoi, `
                + `${status.conflictCount} refusé(s) par le serveur`;
        }

        // The header pill. Read from the SAME snapshot as the bell and the
        // banners, one line below them, so the three can never disagree: the
        // bell counts what is outstanding, the pill says in one word whether
        // anything can leave this device at all.
        //
        // It ships `.hidden` and is revealed HERE, on the first render — not in
        // the markup. A pill that read « Synchronisé » before the engine had
        // been asked would be claiming the money is at the server on the
        // strength of nothing, which is exactly the failure the banners exist
        // to prevent. Offline outranks pending: a cashier who is offline cannot
        // act on a queue length, and « 3 en attente » next to a dead connection
        // reads as a transient delay rather than as work that is going nowhere.
        const syncPill = typeof document !== 'undefined'
            ? document.getElementById('sync-pill')
            : null;
        if (syncPill) {
            let pillState = 'synced';
            let pillText = 'Synchronisé';
            if (!status.online) {
                pillState = 'offline';
                pillText = 'Hors ligne';
            } else if (status.pendingCount > 0) {
                pillState = 'pending';
                pillText = `${status.pendingCount} en attente`;
            }
            syncPill.dataset.state = pillState;
            syncPill.textContent = pillText;
            syncPill.classList.remove('hidden');
        }

        const onlineStatus = typeof document !== 'undefined'
            ? document.getElementById('online-status')
            : null;
        if (onlineStatus) {
            onlineStatus.textContent = status.online
                ? `Système en ligne — synchronisé ${formatInstant(status.lastPullAt)}`
                : 'Système hors ligne';
        }
    },

    /** Stop publishing (sign-out). The engine's own lifecycle is `shutdownEngine`. */
    stop() {
        if (this.refreshTimer != null) {
            clearInterval(this.refreshTimer);
            this.refreshTimer = null;
        }
        if (this.unsubscribe) {
            this.unsubscribe();
            this.unsubscribe = null;
        }
        if (this.connectivityHandler && typeof window !== 'undefined') {
            window.removeEventListener('online', this.connectivityHandler);
            window.removeEventListener('offline', this.connectivityHandler);
            this.connectivityHandler = null;
        }
        this.started = false;
    }
};
